"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var axios_1 = __importDefault(require("axios"));
/**************************************** PROJECT MODULES *****************************************/
var javelin_exports_1 = require("../javelin-exports");
var records_1 = require("../data/records");
var javelin_impl_1 = require("../javelin-impl");
var javelin_json_1 = require("../json/javelin-json");
var javelin_data_1 = require("../data/javelin-data");
var javelin_sync_1 = require("../sync/javelin-sync");
var api_routes_1 = require("../config/api-routes");
var store_1 = require("../data/store/store");
/******************************************** LOGGING *********************************************/
var shared_1 = require("mad-logs/lib/shared");
var log = shared_1.logFactory('javelin-factory.ts', shared_1.Styles.aquarium);
/******************************************* CONSTANTS ********************************************/
var PLATFORM_WEB = 2;
var DEFAULT_LANG = "en";
/************************* STORED JAVELIN MANAGER & INTERFACE SINGLETONS **************************/
/**
 * Memoized JavelinManager & JavelinData instances
 */
var _javelin = {};
/**
 * Memoized SyncManager
 */
var _syncManager = null;
/**************************************** RAW DATA LOADING ****************************************/
/**
 * Memoized raw source data for initializing Javelin (e.g. schedules, diseases)
 * Used to avoid loading data from remote source more than once
 */
var rawSrcData = {};
/**
 * Load data (for initializing Javelin) from remote sources using URL config,
 * and memoize it
 * Example data loaded: schedules, diseases, disease-concepts, vaccines
 */
var getRawData = function (urlConfigs, dataType) {
    if (rawSrcData[dataType])
        return Promise.resolve(rawSrcData[dataType]);
    var _a = urlConfigs[dataType], method = _a.method, url = _a.url;
    log.info("getRawData :: type: " + dataType + " -- method:", method, "URL:", url);
    return axios_1.default
        .get(url + "?timestamp=" + new Date().getTime())
        .then(function (res) { return res.data; })
        .then(function (data) {
        rawSrcData[dataType] = data;
        return data;
    })
        .catch(function (error) {
        return log.error("getRawData: Failed to get " + dataType + " data by " + method + " request to " + url + ": error:", error);
    });
};
/********************************* JAVELIN COMPONENT INITIALIZERS *********************************/
/**
 * Build, configure, memoize, and return JavelinManager & dependency JavelinData
 *
 * Set JavelinManager instance's current lang & string translations function
 *
 * Also builds & binds other JavelinManager dependencies: JavelinImpl (the root
 * Javelin interface), JsonInterface, and JavelinData
 *
 * @returns {Object<javelinManager: JavelinManagerInstance, javelinData: JavelinDataInstance>}
 */
var initJavelinManager = function (rawContentData, rawDiseasesData, rawVaccinesData, rawPHUConfigData, appVersion, translateFn, curLang) {
    // Return existing Javelin if it's already built (Javelin is singleton)
    if (Object.keys(_javelin).length !== 0)
        return _javelin;
    // Create JavelinData adapter (interface)
    _javelin.javelinData = new javelin_data_1.JavelinData(rawContentData, rawDiseasesData, rawVaccinesData, rawPHUConfigData);
    // Init JavelinManager, including setting def lang & building JavelinManager subcomponents
    _javelin.javelinManager = javelin_exports_1.javelinManagerFactory(PLATFORM_WEB, new javelin_impl_1.JavelinImpl(appVersion, translateFn), new javelin_json_1.JavelinJson(), javelin_exports_1.JavelinDate.localNow(), _javelin.javelinData);
    // Bind current language
    _javelin.javelinManager.language = curLang || DEFAULT_LANG;
    return _javelin;
};
/**
 * Build, memoize, & return [SyncManager]: a singleton interface object handling
 * syncing of account & record data between client and server
 *
 * Also instantiates JavelinSync (JVLNS' SyncInterface implementation) & binds
 * it to syncManager
 *
 * @param {Object} urlConfigs Configuration containing locations of remote data
 *                            requried to hydrate Javelin
 * @returns {SyncManagerInstance} Interface object to sync app data with server
 */
var syncManagerFactory = function (urlConfigs) {
    if (_syncManager)
        return _syncManager;
    // Build & bind javelinSync to syncManager
    var javelinSync = new javelin_sync_1.JavelinSync(urlConfigs);
    _syncManager = new javelin_exports_1.SyncManager(javelinSync);
    log.info("syncManagerFactory :: _syncManager:", _syncManager);
    // Bind javelinSync back to syncManager
    javelinSync.initialize(_syncManager);
    return _syncManager;
};
/**
 * Full setup sequence for javelin object (minus app- and record-specific data)
 *
 * Activate with [urlConfigs] object containing URL & request type for each sync
 * server route, and a translator function ([translateFn]) for converting string
 * IDs into localized text
 *
 * @param {string} appVersion Conglomerate version of the app - should contain
 *                            app version, build number, & release environment
 * @param {Object} urlConfigs Object with URL & associated request type for
 *                            each sync server route
 * @param {Function} translateFn Converts string IDs -> localized display text
 * @return {{SyncManager,JavelinManager, JavelinData, DataManager}} Javelin obj instances
 */
var initJavelinSequence = function (appVersion, urlConfigs, translateFn, curLang) {
    if (urlConfigs === void 0) { urlConfigs = api_routes_1.apiRoutes; }
    var initStore = store_1.store
        .initAsync()
        .catch(function (err) { return log.error("initJavelinSequence :: Failed to initialize store: err:", err); });
    // Load all raw data from remote locations
    return Promise.all([
        getRawData(urlConfigs, "content"),
        getRawData(urlConfigs, "diseases"),
        getRawData(urlConfigs, "vaccines"),
        getRawData(urlConfigs, "phu"),
        initStore,
    ])
        .then(function (_a) {
        var rawContentData = _a[0], rawDiseasesData = _a[1], rawVaccinesData = _a[2], rawPHUConfigData = _a[3], _ = _a[4];
        var syncManager = syncManagerFactory(urlConfigs);
        var _b = initJavelinManager(rawContentData, rawDiseasesData, rawVaccinesData, rawPHUConfigData, appVersion, translateFn, curLang), javelinManager = _b.javelinManager, javelinData = _b.javelinData;
        // Ensure javelinManager isn't already bound to syncManager
        if (!javelinManager.syncManager) {
            // Feed fully-populated SyncManager object to JavelinManager
            javelinManager.addSyncManager(syncManager);
        }
        return {
            syncManager: syncManager,
            javelinManager: javelinManager,
            javelinData: javelinData,
            dataManager: javelinManager.dataManager,
        };
    })
        .catch(function (error) {
        log.error("initJavelinSequence :: Failed to initialize Javelin with error:", error);
        throw error;
    });
};
/*********************************** BUILD ROOT JAVELIN OBJECT ************************************/
/**
 * Memoized result of javelinFactory
 */
var _javelinFacade = null;
/**
 * Build Javelin interface object from object returned by initJavelinSequence
 */
var buildJavelinFacade = function (initializedJavelin) {
    var syncManager = initializedJavelin.syncManager, javelinData = initializedJavelin.javelinData, javelinManager = initializedJavelin.javelinManager;
    return {
        syncManager: syncManager,
        javelinData: javelinData,
        javelinManager: javelinManager,
        dataManager: javelinManager.dataManager,
        // Record constructor
        buildRecord: records_1.getRecordFromSchedules(javelinData.store
            .schedules),
        // Add a new record to the store
        addRecord: javelinData.store.addOrUpdateRecord,
    };
};
/**
 * Build memoized singleton interface object for interacting with Javelin from
 * outside
 *
 * Activate using config object [props], containing [urlConfigs] object with
 * URL & request type for each sync server route, translator function
 * [translateFn] for converting string IDs into localized display text,
 * [currentLanguage] ID (e.g. `en`), and [clientVersion] string containing the
 * app version, build number, and build environment
 *
 * @param {Object} props Config object for activating Javelin (see description)
 * @return {Object} Facade object for interacting with Javelin - containing
 *                  JavelinManager, SyncManager, JavelinData, DataManager, and
 *                  functions to add & build records (addRecord, buildRecord)
 */
exports.javelinFactory = function (props) {
    var translateFn = props.translateFn, clientVersion = props.clientVersion, currentLanguage = props.currentLanguage;
    // Return memoized version if present
    if (_javelinFacade)
        return Promise.resolve(_javelinFacade);
    return (
    // Initialize Javelin, return raw Javelin object (with remotely loaded)
    initJavelinSequence(clientVersion, api_routes_1.apiRoutes, translateFn, currentLanguage)
        // Use source data to build object for interfacing with Javelin
        .then(function (data) {
        _javelinFacade = buildJavelinFacade(data);
        return _javelinFacade;
    })
        .catch(function (error) {
        log.error("javelinFactory :: Failed to initialize Javelin - error:", error);
        throw error;
    }));
};
//# sourceMappingURL=javelin-factory.js.map